/**
 * BrandLoader — the Desktop splash, as a component.
 *
 * BigxBookDesktop shows a branded splash while Electron cold-starts (see
 * electron/main.ts createSplash): the Bigxstar mark over a thin indeterminate
 * bar on a white sheet. Web and Mobile had no equivalent — every wait was an
 * ad-hoc spinner or a bare "Loading…" — so the three products looked like
 * three different applications during the moment users see most often.
 *
 * The geometry is copied from the Electron splash deliberately, so the three
 * stay identical: 128px mark, 26px gap, a 150x3 track in #eef0f4 carrying a
 * 38%-wide #2563eb indicator that slides -120% -> 380% over 1.1s.
 *
 * An indeterminate bar is the honest control here: none of these waits (DB
 * open, migration, first sync) report progress, and a percentage that cannot
 * be computed is worse than one that is not claimed.
 */
import { cn } from '@/utils/cn';

export function BrandLoader({
  /** Fills the viewport. Use for boot and route-level waits. */
  fullscreen = true,
  /** Optional caption. Keep it short — it sits under the bar. */
  label,
  /** Scales the mark; the bar keeps its proportions. */
  size = 128,
  className,
}: {
  fullscreen?: boolean;
  label?: string;
  size?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center bg-white',
        fullscreen ? 'fixed inset-0 z-50' : 'h-full w-full py-10',
        className,
      )}
      // Announce as a busy region so assistive tech does not read the mark as
      // meaningful content while the app is still coming up.
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <img
        src="/brand/bigxstar-mark.png"
        alt=""
        aria-hidden
        draggable={false}
        className="select-none object-contain"
        style={{ width: size, height: size }}
      />

      <div className="bb-loader-track" style={{ marginTop: Math.round(size * 0.203) }}>
        <i className="bb-loader-indicator" />
      </div>

      {label ? (
        <p className="mt-4 text-sm text-slate-500">{label}</p>
      ) : (
        <span className="sr-only">Loading</span>
      )}
    </div>
  );
}
